import { ArrowDownwardRounded } from '@mui/icons-material'
import { Fade } from '@mui/material'
import { Box } from '@mui/system'
import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'

import { AuthContextUser } from 'torqcloud-ui/build/contexts/AuthContext'

import { MessageFeedbackValue } from '@api/agentBuilder/chatmessage'
import { InternalMessage } from '@slices/userMessage'
import { handleSanitizedCopy } from '@/utils/sanitizedCopy'
import { MarkdownCollapseContext } from './Chat.Markdown'
import { chatScrollbarSx } from './chatScrollbar'
import { ChatFileResponse } from '@api/generated/internal/internalApi'
import Message from './Chat.Message'
import LoadingMessage from './Chat.Message.Loading'

import './index.css'

// Threshold in pixels - if user is within this distance from bottom, auto-scroll continues
const AUTO_SCROLL_THRESHOLD = 450
// Minimum time between scroll operations during streaming (ms)
const SCROLL_THROTTLE_MS = 200

export interface ChatProps {
  chatflowId: string
  /** The session's generated files, forwarded to tool result cards so they can show their files. */
  sessionFiles?: ChatFileResponse[]
  user?: AuthContextUser
  messages?: InternalMessage[]
  showLoadingMessage?: boolean
  canSubmitMessageFeedback?: boolean
  onSubmitMessageFeedback?: (feedback: { messageId: string; text?: string | null; value?: MessageFeedbackValue | null }) => Promise<boolean>
  onApproveToolCall?: (message: InternalMessage) => Promise<void>
  onRejectToolCall?: (message: InternalMessage, reason?: string) => Promise<void>
  onCancelSubTeammate?: (message: InternalMessage, reason?: string) => Promise<void>
  onAcceptSubTeammate?: (message: InternalMessage) => Promise<void>
  onSubmitSubTeammateFollowUp?: (message: InternalMessage, followUp: string) => Promise<void>
  collapseMarkdown?: boolean
  /**
   * Uses a thin scrollbar instead of the native one, for narrow panels such as the Workforce Assistant
   * where a full-width bar crowds the messages.
   */
  showScrollbar?: boolean
}

export interface ChatRef {
  /**
   * Scroll to the first message
   */
  scrollToFirst: () => void
  /**
   * Scroll to the latest message
   */
  scrollToLatest: () => void
  /**
   * Scroll to a specific message
   */
  scrollToMessage: (messageId: string) => void
}

function Chat(
  {
    chatflowId,
    sessionFiles,
    user,
    messages = [],
    showLoadingMessage,
    canSubmitMessageFeedback,
    onSubmitMessageFeedback,
    onApproveToolCall,
    onRejectToolCall,
    onCancelSubTeammate,
    onAcceptSubTeammate,
    onSubmitSubTeammateFollowUp,
    collapseMarkdown = true,
    showScrollbar = false,
  }: ChatProps,
  ref: React.Ref<ChatRef>
) {
  const messageTopRef = useRef<HTMLDivElement>(null)
  const messageBotRef = useRef<HTMLDivElement>(null)
  const messagesRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isScrollable, setIsScrollable] = useState<boolean>(false)

  // Track whether user is near the bottom (for auto-scroll behavior)
  const isNearBottomRef = useRef<boolean>(true)
  // Throttle scroll operations during streaming
  const lastScrollTimeRef = useRef<number>(0)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Check if user is near the bottom of the scroll container
  const checkIfNearBottom = useCallback(() => {
    const container = containerRef.current
    if (!container) return true

    const { scrollTop, scrollHeight, clientHeight } = container
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    return distanceFromBottom <= AUTO_SCROLL_THRESHOLD
  }, [])

  // Perform the actual scroll operation
  const doScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    lastScrollTimeRef.current = Date.now()
    container.scrollTo({ top: container.scrollHeight, behavior: 'auto' })
  }, [])

  useEffect(() => {
    const target = messagesRef.current
    if (!target) return

    const obs = new ResizeObserver(() => {
      setIsScrollable(!isScrolledToBottom(containerRef.current))
    })

    obs.observe(target)

    return () => {
      obs.disconnect()
    }
  }, [])

  // Smooth auto-scroll with throttling - only scrolls if user is near bottom
  useLayoutEffect(() => {
    if (!messageBotRef.current || !containerRef.current) return

    // Only auto-scroll if user is near the bottom
    if (!isNearBottomRef.current) return

    const now = Date.now()
    const timeSinceLastScroll = now - lastScrollTimeRef.current

    // If we're within the throttle period, schedule a scroll for when it ends
    if (timeSinceLastScroll < SCROLL_THROTTLE_MS) {
      // Only schedule if we don't already have a pending timeout
      if (!scrollTimeoutRef.current) {
        const timeUntilNextScroll = SCROLL_THROTTLE_MS - timeSinceLastScroll
        scrollTimeoutRef.current = setTimeout(() => {
          scrollTimeoutRef.current = null
          // Re-check if user is still near bottom before scrolling
          if (isNearBottomRef.current) {
            doScroll()
          }
        }, timeUntilNextScroll)
      }
      // If timeout already pending, do nothing - it will handle the scroll
    } else {
      // Throttle period has passed, scroll immediately
      doScroll()
    }
  }, [messages, doScroll])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const handleOnScroll = useCallback(() => {
    const messagesBot = messageBotRef.current
    if (!messagesBot) return

    setIsScrollable(!isScrolledToBottom(containerRef.current))

    // Update whether user is near bottom (for auto-scroll decision)
    isNearBottomRef.current = checkIfNearBottom()
  }, [checkIfNearBottom])

  useImperativeHandle(
    ref,
    () => ({
      scrollToFirst: () => messageTopRef.current?.scrollIntoView({ behavior: 'smooth' }),
      scrollToLatest: () => messageBotRef.current?.scrollIntoView({ behavior: 'smooth' }),
      scrollToMessage: (messageId: string) => {
        const target = containerRef.current?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`)
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      },
    }),
    []
  )

  return (
    <Box
      component="div"
      ref={containerRef}
      data-component="chat"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        height: '100%',
        justifyContent: 'space-between',
        position: 'relative',
        overflowY: 'auto',
        minWidth: 0,
        // WF-2606: the native scroll bar is deliberately left visible here. The
        // scroll-to-bottom button below is a complement, not a replacement: it
        // only appears when the bottom is out of view, only jumps to the end,
        // and conveys no scroll position or conversation length. Suppressing
        // the scroll bar also overrode the user's OS "always show scroll bars"
        // setting, leaving keyboard arrows as the only way to navigate.
        ...chatScrollbarSx(showScrollbar),
        WebkitOverflowScrolling: 'touch',
      }}
      onScroll={handleOnScroll}
    >
      <Box
        component="div"
        ref={messagesRef}
        data-component="chat__messages"
        // Strip the bubble styling from the clipboard so pasting into Word and
        // friends doesn't carry the chat background over (WF-2609). Handled
        // here, rather than per bubble, so selections spanning several
        // messages are covered too.
        onCopy={handleSanitizedCopy}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          justifyContent: 'space-between',
        }}
      >
        <Box ref={messageTopRef} data-component="chat__messages-top" />
        <MarkdownCollapseContext.Provider value={collapseMarkdown}>
          {messages
            .filter(m => m.type !== 'toolCall')
            .filter(m => !m.parentToolCallId)
            .map((m, i) => (
              <Box key={m.id ?? i} data-message-id={m.id} sx={{ scrollMarginBlock: '24px' }}>
                <Message
                  chatflowId={chatflowId}
                  sessionFiles={sessionFiles}
                  message={m}
                  allMessages={messages}
                  isDialog
                  hasMessageStream={false}
                  avatar={user?.avatar}
                  canSubmitMessageFeedback={canSubmitMessageFeedback}
                  onSubmitMessageFeedback={onSubmitMessageFeedback}
                  onApproveToolCall={onApproveToolCall}
                  onRejectToolCall={onRejectToolCall}
                  onCancelSubTeammate={onCancelSubTeammate}
                  onAcceptSubTeammate={onAcceptSubTeammate}
                  onSubmitSubTeammateFollowUp={onSubmitSubTeammateFollowUp}
                />
              </Box>
            ))}
        </MarkdownCollapseContext.Provider>
        {showLoadingMessage && <LoadingMessage />}
        <Box ref={messageBotRef} data-component="chat__messages-bottom" />
      </Box>
      <Box
        sx={{
          display: 'flex',
          position: 'sticky',
          justifyContent: 'end',
          flex: '0 0 40px',
          zIndex: 100000,
          cursor: 'pointer',
          bottom: 15,
          left: '100%',
          width: '40px',
        }}
      >
        <Fade in={isScrollable}>
          <Box
            sx={{
              height: 40,
              width: 40,
              borderRadius: '100%',
              background: t => t.palette.canvasHeader.primaryDark,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={() =>
              messageBotRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
              })
            }
          >
            <ArrowDownwardRounded />
          </Box>
        </Fade>
      </Box>
    </Box>
  )
}

function isScrolledToBottom(container?: HTMLElement | null) {
  if (!container) return true

  return container.scrollHeight - container.scrollTop - container.clientHeight <= 1
}

export default React.forwardRef<ChatRef, ChatProps>(Chat)
